import { assert, isDefined } from '@sama/js-core';
import { mapValues, partition } from 'lodash';
import { contentUtils2D } from '../../../2d/exported/content-utils-2d';
import { contentUtils3D } from '../../../3d/exported/content-utils-3d';
import { ShapeLocationState } from '../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { isPointCloudOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import {
  ContentUtils,
  PartiallyTransformedShape,
} from '../../../team-apis/content/content-utils';
import {
  ShapeEntityExternal,
  ShapeLocationEntityExternal,
} from '../../../team-apis/otterdam/annotations';
import {
  PointType,
  ShapeType,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { AnnotationOutput } from '../../../team-apis/otterdam/outputs/annotation-output';
import {
  appendDynamicTag,
  isShapeEntity,
} from '../utils/annotation-external-utils';

type PartiallyTransformedShapeByIndex = StrictRecord<
  ShapeState['index'],
  PartiallyTransformedShape<PointType, ShapeType>
>;

export class ShapeExternalToInternalConverter {
  constructor(private contentUtils: ContentUtils<PointType, ShapeType>) {}

  convertToInternal(
    annotations: Array<ShapeEntityExternal | ShapeLocationEntityExternal>,
    output: AnnotationOutput,
  ) {
    const [shapes, locations] = partition(annotations, isShapeEntity);

    const partiallyTransformedShapes: PartiallyTransformedShapeByIndex = {};
    for (const shape of shapes) {
      partiallyTransformedShapes[shape.shapeId] =
        extractInfoFromShapeExternal(shape);
    }

    this.addLocationsToShapes(partiallyTransformedShapes, locations);

    return this.completeShapeConversion(partiallyTransformedShapes, output);
  }

  private completeShapeConversion(
    partiallyTransformedShapes: PartiallyTransformedShapeByIndex,
    output: AnnotationOutput,
  ) {
    return mapValues(
      partiallyTransformedShapes,
      (partiallyTransformedShape) => {
        assert(
          partiallyTransformedShape,
          'partiallyTransformedShape should be defined',
        );
        const shape = this.contentUtils.addKeyLocationsToShapeExternal(
          partiallyTransformedShape,
        );
        shape.outputName = output.name;
        return shape;
      },
    );
  }

  private addLocationsToShapes(
    partiallyTransformedShapes: PartiallyTransformedShapeByIndex,
    locations: ShapeLocationEntityExternal[],
  ) {
    // Sort the annotations by frame number since we always want mle frames to
    // be attached to the previous non-extrapolated frame
    const sortedLocations = locations.sort(
      (a, b) => a.frameNumber - b.frameNumber,
    );

    for (const location of sortedLocations) {
      const { shapeId } = location;
      const currentShape = partiallyTransformedShapes[shapeId];
      assert(currentShape, `Shape ${shapeId} should be defined for location`);

      if (location.points) {
        currentShape.key_locations = this.appendKeyLocation(
          currentShape.key_locations,
          location,
        );
      }
      if (location.tags) {
        currentShape.dynamic_tags = appendDynamicTag(
          location,
          currentShape.dynamic_tags,
        );
      }
    }
  }

  private appendKeyLocation(
    keyLocations: Array<ShapeLocationState<PointType>>,
    shapeLocation: ShapeLocationEntityExternal,
  ) {
    const updatedKeyLocations = [...keyLocations];
    const source = shapeLocation.source;
    const points = shapeLocation.points;
    assert(points, 'Points for shape location external should be defined');

    if (source) {
      const lastKeyLocation = keyLocations[keyLocations.length - 1];
      lastKeyLocation.extrapolation ??= {
        source,
        points: [],
      };
      lastKeyLocation.extrapolation.points.push(points);
    } else {
      const visibility = shapeLocation.visibility;
      assert(
        isDefined(visibility),
        'Visibility should be defined when we have a key location',
      );
      updatedKeyLocations.push({
        points,
        visibility,
        frame_number: shapeLocation.frameNumber,
      });
    }

    return updatedKeyLocations;
  }
}

const answerHelper3D = new ShapeExternalToInternalConverter(contentUtils3D);

const answerHelper2D = new ShapeExternalToInternalConverter(contentUtils2D);

function extractInfoFromShapeExternal(
  annotation: ShapeEntityExternal,
): ShapeState {
  const { type, objectId, tags, shapeId, linkedShapeId } = annotation;
  return {
    type,
    parentId: objectId,
    tags: tags || {},
    index: shapeId,
    dynamic_tags: {},
    key_locations: [],
    ...(linkedShapeId ? { linkedShapeId } : {}),
  };
}

export function convertShapesToInternal(
  annotations: Array<ShapeEntityExternal | ShapeLocationEntityExternal>,
  output: AnnotationOutput,
) {
  const annotationsForOutput = annotations.filter(
    (annotation) => annotation.outputId === output.id,
  );
  const answerHelper = isPointCloudOutput(output)
    ? answerHelper3D
    : answerHelper2D;
  return answerHelper.convertToInternal(annotationsForOutput, output);
}
